import { Box, Group, Text, UnstyledButton } from '@mantine/core';
import { modals } from '@mantine/modals';
import moment from 'moment';

import { EORIGIN_DATE_FORMAT_WITH_TIME, Importer, POADocumentType, RelatedResource, RelatedResourceStatus } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { Table, TableActions, TableColumnProps } from '@components';
import { useDeleteDocument, useDownloadDocument, useGenerateDocument, useSendDocument } from '@hooks/importer';
import { useSettings } from '@hooks/useSettings';
import classes from './ImporterViewPOA.module.css';

interface POADocument extends RelatedResource {
  type: POADocumentType;
}

interface ImporterViewPOAProps {
  importer: Importer;
}

// eslint-disable-next-line max-statements
export const ImporterViewPOA: React.FC<ImporterViewPOAProps> = ({ importer }) => {
  const { data: settings } = useSettings();
  const generateDocumentMutation = useGenerateDocument();
  const deleteDocumentMutation = useDeleteDocument();
  const downloadDocumentMutation = useDownloadDocument();
  const sendDocumentMutation = useSendDocument();

  const generateDocument = (row: POADocument) => generateDocumentMutation.mutate({ importerId: importer._id, type: row.type });

  const downloadDocument = (row: POADocument) => downloadDocumentMutation.mutate({ importerId: importer._id, fileName: row.fileName });

  const openDeleteDocument = (row: POADocument) => {
    modals.openConfirmModal({
      title: 'Delete document',
      children: <Text>Do you really want to delete the document?</Text>,
      labels: { confirm: 'Yes', cancel: 'No' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteDocumentMutation.mutate({ importerId: importer._id, fileName: row.fileName, type: row.type });
      },
    });
  };

  const openSendDocument = (row: POADocument) => {
    modals.openConfirmModal({
      title: 'Send document',
      children: <Text>Do you really want to send the document?</Text>,
      labels: { confirm: 'Yes', cancel: 'No' },
      onConfirm: () => {
        sendDocumentMutation.mutate({ importerId: importer._id, type: row.type });
      },
    });
  };

  const documents: POADocument[] = [
    ...(importer?.poa?.relatedResources?.length
      ? importer.poa.relatedResources
      : [{ fileName: 'Create POA document', status: RelatedResourceStatus.EMPTY }]
    ).map((r) => ({ ...r, type: POADocumentType.POA })),
    ...(importer?.vatGuarantee?.relatedResources?.length
      ? importer.vatGuarantee.relatedResources
      : [{ fileName: 'Create VAT Guarantee document', status: RelatedResourceStatus.EMPTY }]
    ).map((r) => ({ ...r, type: POADocumentType.VATGuarantee })),
  ];

  const columns: TableColumnProps<POADocument>[] = [
    {
      name: 'Identification document',
      width: '20%',
      cell: (row) => (
        <Text fz={13} fw={700} c={'#1870A3'}>
          {row.type}
        </Text>
      ),
    },
    {
      name: 'File name',
      cell: (row) => (row.issuedBy === 'SELLER' ? 'POA signed by importer' : 'Original generated POA'),
      width: '20%',
    },
    {
      name: 'Date',
      width: '20%',
      cell: (row) => (row.issuedAt ? moment(row.issuedAt).format(EORIGIN_DATE_FORMAT_WITH_TIME) : ''),
    },
    {
      name: 'Status',
      cell: (row) => row.status,
      width: '20%',
    },
    {
      name: 'Actions',
      sticky: 'right',
      hide: settings.isForwarder,
      cell: (row) => (
        <Group>
          {row.status === RelatedResourceStatus.EMPTY && (
            <UnstyledButton
              className={classes.button}
              onClick={(evt) => {
                evt.preventDefault();
                generateDocument(row);
              }}
            >
              Generate {row.type}
            </UnstyledButton>
          )}
          {row.status === RelatedResourceStatus.NOT_SENT && (
            <UnstyledButton
              className={classes.button}
              onClick={(evt) => {
                evt.preventDefault();
                openSendDocument(row);
              }}
            >
              Send my signed {row.type} file
            </UnstyledButton>
          )}
          {row.status !== RelatedResourceStatus.EMPTY && (
            <TableActions
              actions={[
                {
                  icon: IconFactory.download,
                  text: 'Download file',
                  onClick: () => downloadDocument(row),
                },
                {
                  icon: IconFactory.delete,
                  text: 'Delete',
                  onClick: () => openDeleteDocument(row),
                },
              ]}
            />
          )}
        </Group>
      ),
    },
  ];

  return (
    <Box p="lg">
      <Table columns={columns} dataSource={documents} pagination verticalSpacing="xs" />
    </Box>
  );
};
